import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, Loader2, AlertCircle, X } from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const STROKE = 1.5;

function NewCandidateDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/candidates", {
        name,
        email: email || undefined,
        linkedin_url: linkedin || undefined,
      });
      onCreated(data);
      setName(""); setEmail(""); setLinkedin("");
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add candidate");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="new-candidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">New candidate</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-500">
            Add a candidate to your workspace. You can attach interviews to them after recording.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2" data-testid="new-candidate-form">
          <div>
            <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">Name</label>
            <input required className="hp-input mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="candidate-name" />
          </div>
          <div>
            <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">Email (optional)</label>
            <input type="email" className="hp-input mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="candidate-email" />
          </div>
          <div>
            <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">LinkedIn URL (optional)</label>
            <input className="hp-input mt-1.5" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} data-testid="candidate-linkedin" />
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="candidate-error">
              <AlertCircle className="mt-0.5 h-4 w-4" strokeWidth={STROKE} /> {error}
            </div>
          )}
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="hp-btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className="hp-btn-primary" data-testid="candidate-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
              Add candidate
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Candidates() {
  const { member } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/candidates").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const canCreate = ["admin", "recruiter"].includes(member?.role);

  return (
    <AppShell>
      <div data-testid="candidates-page" className="space-y-8">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="hp-section-label">Candidates</div>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">Candidates</h1>
            <p className="mt-1 text-[14px] text-ink-500">
              Every candidate in your workspace. Click one for the full interview timeline.
            </p>
          </div>
          {canCreate && (
            <button onClick={() => setDialogOpen(true)} className="hp-btn-primary" data-testid="new-candidate-btn">
              <Plus className="h-4 w-4" strokeWidth={STROKE} /> New candidate
            </button>
          )}
        </header>

        {loading && (
          <div className="hp-card flex justify-center p-12">
            <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="hp-card flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="candidates-empty">
            <Users className="h-6 w-6 text-ink-300" strokeWidth={STROKE} />
            <h2 className="font-display mt-4 text-xl font-medium text-ink-900">No candidates yet</h2>
            <p className="mt-2 max-w-md text-[14px] text-ink-500">
              Add a candidate to track their progress across rounds and link interview recordings to them.
            </p>
            {canCreate && (
              <button onClick={() => setDialogOpen(true)} className="hp-btn-primary mt-6" data-testid="candidates-empty-cta">
                <Plus className="h-4 w-4" strokeWidth={STROKE} /> Add the first candidate
              </button>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="hp-card overflow-hidden">
            <table className="w-full" data-testid="candidates-table">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Added</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {items.map((c) => (
                  <tr key={c.candidate_id} onClick={() => navigate(`/candidates/${c.candidate_id}`)}
                      className="hover:bg-ink-50 transition cursor-pointer"
                      data-testid={`candidate-row-${c.candidate_id}`}>
                    <td className="px-5 py-3 text-[14px] font-medium text-ink-900">{c.name}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-700">{c.email || "—"}</td>
                    <td className="px-5 py-3 text-[13px] text-ink-700 capitalize">{c.current_status}</td>
                    <td className="px-5 py-3 text-[12px] text-ink-500">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right text-[13px] text-emerald-800">Open →</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <NewCandidateDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={() => load()} />
    </AppShell>
  );
}
